import { Component, OnInit } from '@angular/core';

type InteractionMode = 'primary' | 'secondary';
type LegacyToggleKey =
  | 'global-layout'
  | 'list-utilities'
  | 'modal-header-legacy'
  | 'md-input-compat'
  | 'aligndetails-compat';

interface LegacyToggleDefinition {
  key: LegacyToggleKey;
  label: string;
  helperText: string;
}

@Component({
  selector: 'app-style-state-toggle',
  templateUrl: './style-state-toggle.component.html',
  styleUrls: ['./style-state-toggle.component.scss']
})
export class StyleStateToggleComponent implements OnInit {
  private static readonly storageKey = 'lms.interaction.mode';
  private static readonly legacyStorageKey = 'lms.legacy.compat.enabled';
  private static readonly legacyToggleStoragePrefix = 'lms.legacy.toggle.';
  private static readonly legacyToggleClassPrefix = 'legacy-toggle-';

  readonly legacyToggleDefinitions: LegacyToggleDefinition[] = [
    {
      key: 'global-layout',
      label: 'Global layout',
      helperText: 'Affects shared button widths, modal body tint, and table container spacing on form/report screens.'
    },
    {
      key: 'list-utilities',
      label: 'List utilities',
      helperText: 'Affects list wrapper widths and card-like list containers in list/viewer-heavy pages.'
    },
    {
      key: 'modal-header-legacy',
      label: 'Modal header legacy',
      helperText: 'Affects centered legacy panel/modal headings and title alignment in older dialog layouts.'
    },
    {
      key: 'md-input-compat',
      label: 'MD input compatibility',
      helperText: 'Affects legacy md-input and forminput borders/spacing; verify client details and data-entry forms.'
    },
    {
      key: 'aligndetails-compat',
      label: 'Align details compatibility',
      helperText: 'Affects vertical alignment behavior for aligndetails host blocks in detail sections.'
    },
  ];

  mode: InteractionMode = 'primary';

  legacyCompatEnabled = true;
  legacyToggles: Record<LegacyToggleKey, boolean> = {
    'global-layout': true,
    'list-utilities': true,
    'modal-header-legacy': true,
    'md-input-compat': true,
    'aligndetails-compat': true,
  };

  ngOnInit(): void {
    const saved = localStorage.getItem(StyleStateToggleComponent.storageKey);
    if (saved === 'secondary' || saved === 'primary') {
      this.mode = saved;
    }

    const legacySaved = localStorage.getItem(StyleStateToggleComponent.legacyStorageKey);
    if (legacySaved === 'false') {
      this.legacyCompatEnabled = false;
    }

    this.legacyToggleDefinitions.forEach((toggle) => {
      const storageKey = `${StyleStateToggleComponent.legacyToggleStoragePrefix}${toggle.key}`;
      const savedToggle = localStorage.getItem(storageKey);
      if (savedToggle === 'true' || savedToggle === 'false') {
        this.legacyToggles[toggle.key] = savedToggle === 'true';
      }
    });

    this.applyMode(this.mode);
    this.applyLegacyMode();
  }

  onModeChange(useSecondary: boolean): void {
    this.mode = useSecondary ? 'secondary' : 'primary';
    localStorage.setItem(StyleStateToggleComponent.storageKey, this.mode);
    this.applyMode(this.mode);
  }

  isSecondaryMode(): boolean {
    return this.mode === 'secondary';
  }

  onLegacyModeChange(enabled: boolean): void {
    this.legacyCompatEnabled = enabled;
    localStorage.setItem(StyleStateToggleComponent.legacyStorageKey, String(enabled));
    this.applyLegacyMode();
  }

  onLegacyToggleChange(toggleKey: LegacyToggleKey, enabled: boolean): void {
    this.legacyToggles[toggleKey] = enabled;
    localStorage.setItem(
      `${StyleStateToggleComponent.legacyToggleStoragePrefix}${toggleKey}`,
      String(enabled)
    );
    this.applyLegacyMode();
  }

  resetToDefault(): void {
    localStorage.removeItem(StyleStateToggleComponent.storageKey);
    localStorage.removeItem(StyleStateToggleComponent.legacyStorageKey);
    this.mode = 'primary';
    this.legacyCompatEnabled = true;
    this.legacyToggleDefinitions.forEach((toggle) => {
      this.legacyToggles[toggle.key] = true;
      localStorage.removeItem(`${StyleStateToggleComponent.legacyToggleStoragePrefix}${toggle.key}`);
    });
    this.applyMode(this.mode);
    this.applyLegacyMode();
  }

  private applyMode(mode: InteractionMode): void {
    const root = document.documentElement;

    if (mode === 'secondary') {
      root.style.setProperty('--lms-interaction-color', 'var(--secondary)');
      root.style.setProperty('--lms-interaction-bg', 'rgba(236, 64, 122, 0.12)');
      root.style.setProperty('--lms-interaction-bg-soft', 'rgba(236, 64, 122, 0.08)');
      root.style.setProperty('--lms-focus-ring-color', '0 0 0 0.2rem rgba(236, 64, 122, 0.25)');
      root.style.setProperty('--lms-input-border-focus', 'var(--secondary)');
      root.style.setProperty('--status-active', 'var(--secondary)');
      root.style.setProperty('--status-active-rgb', '236, 64, 122');
      return;
    }

    root.style.setProperty('--lms-interaction-color', 'var(--primary)');
    root.style.setProperty('--lms-interaction-bg', 'rgba(63, 81, 182, 0.1)');
    root.style.setProperty('--lms-interaction-bg-soft', 'rgba(63, 81, 182, 0.05)');
    root.style.setProperty('--lms-focus-ring-color', '0 0 0 0.2rem rgba(63, 81, 182, 0.25)');
    root.style.setProperty('--lms-input-border-focus', 'var(--primary)');
    root.style.setProperty('--status-active', 'var(--primary)');
    root.style.setProperty('--status-active-rgb', '92, 107, 192');
  }

  private applyLegacyMode(): void {
    this.legacyToggleDefinitions.forEach((toggle) => {
      document.body.classList.remove(`${StyleStateToggleComponent.legacyToggleClassPrefix}${toggle.key}`);
    });

    if (!this.legacyCompatEnabled) {
      return;
    }

    this.legacyToggleDefinitions.forEach((toggle) => {
      if (!this.legacyToggles[toggle.key]) {
        return;
      }

      document.body.classList.add(`${StyleStateToggleComponent.legacyToggleClassPrefix}${toggle.key}`);
    });
  }
}
